import { LOGO } from '../constants/imageTemplates';
import config from '../config';
import { createAuth } from '@justgiving/auth';
import { getLoginPageUrlParameters } from './getLoginPageUrlParameters';
import { BBAuth } from '@blackbaud-internal/auth-client';
import { RedirectToLoginError } from '../components/ErrorBoundary/errors';
import type { BBAuthGetTokenArgs } from '@blackbaud-internal/auth-client/src/auth/auth-get-token-args';

const { authCookieName, activeGroupCookieName, refreshAccessTokenUrl } = config;

export const auth = createAuth({
  documentOrReq: document,
  authCookieName,
  activeGroupCookieName,
  refreshAccessTokenUrl,
});

export const getCharityId = () => {
  const permission = auth.getActivePermission('charity');

  return permission?.groupTypeExternalId
    ? parseInt(permission.groupTypeExternalId, 10)
    : undefined;
};

export const isCampaignAdmin = () => {
  return (
    auth.hasGroupRole('charity', 'admin') ||
    auth.hasGroupRole('charity', 'fundraising')
  );
};

export const getUserFullName = () => {
  const token = auth.getAccessToken();
  return token?.givenName || token?.familyName
    ? `${token.givenName} ${token?.familyName}`
    : null;
};

/**
 * The commerce platform environment id of the charity the session is acting
 * for. Sourced from `@justgiving/auth`'s `getEnvironmentId`, which decodes
 * the active groups cookie written by JG.IdentityAndAccess.Sso's
 * `ActiveGroupService`:
 *
 *   {"environmentId":"p-a1b2c3d4e5f6","charity":"6f9619ff-...","consumer":null}
 *
 * A non-empty value means the charity is migrated, `null` means it is known
 * to be unmigrated, and a missing member means nothing has been recorded yet
 * - the last two are indistinguishable to us and both mean "not migrated".
 *
 * Read fresh on every call and deliberately never cached: switching to a
 * different charity rewrites the cookie, and the legacy path has to come back
 * when that charity is not migrated.
 */
export const getActiveGroupEnvironmentId = (): string | undefined =>
  auth.getEnvironmentId();

/** True when the charity the session is acting for is migrated. */
export const isActiveGroupMigrated = (): boolean =>
  getActiveGroupEnvironmentId() !== undefined;

type GetLoginUrlParams = {
  campaignId?: string;
  campaignTitle?: string;
  causeLogo?: string;
  charityName?: string;
  eventName?: string;
  title?: string;
  logo?: string;
  returnUrl?: string;
};

export function getLoginUrl(params?: GetLoginUrlParams) {
  const ssoUrl = new URL(config.consumerSSOUrl);

  const { returnUrl, ...encodedParams } = params ?? {};

  ssoUrl.searchParams.set('returnUrl', returnUrl ?? window.location.href);
  ssoUrl.searchParams.set('Promo', 'true');
  ssoUrl.searchParams.set('flowtype', 'page_creation');

  if (encodedParams && Object.keys(encodedParams).length > 0) {
    ssoUrl.searchParams.set(
      'EncodedOptions',
      getLoginPageUrlParameters({
        campaignId: encodedParams.campaignId,
        campaignTitle: encodedParams.campaignTitle,
        causeLogo: `${encodedParams.causeLogo}?template=${LOGO}`,
        charityName: encodedParams.charityName,
        eventName: encodedParams.eventName,
        title: encodedParams.title,
      }) ?? ''
    );
  }

  return ssoUrl.toString();
}

export async function getBBToken(bbAuthGetTokenArgs?: BBAuthGetTokenArgs) {

  try {
    return await BBAuth.getToken(bbAuthGetTokenArgs);
  } catch {
    throw new RedirectToLoginError();
  }
}
